import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Bell, Heart, LogIn, User, LogOut, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useFavorites } from '@/contexts/FavoritesContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logoMain from '@/assets/logo-main.png';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { favorites } = useFavorites();
    const { user, logout } = useAuth();
    const { subscription } = useSubscription();

    // Trial Logic
    const trialEndsAt = subscription?.trialEnd ? new Date(subscription.trialEnd).getTime() : 0;
    const isFree = (subscription?.plan || 'esencial') === 'esencial';
    const trialActive = Boolean(user && isFree && trialEndsAt && Date.now() < trialEndsAt);
    const trialDaysLeft = trialActive ? Math.ceil((trialEndsAt - Date.now()) / (24 * 60 * 60 * 1000)) : 0;

    let planLabel = (subscription?.plan || 'FREE').toUpperCase();
    let planColor = 'bg-slate-600';

    if (planLabel === 'PREMIUM') planColor = 'bg-amber-500';
    else if (planLabel === 'PRO') planColor = 'bg-blue-500';

    if (trialActive) {
        planLabel = `TRIAL (${trialDaysLeft}d)`;
        planColor = 'bg-gradient-to-r from-blue-500 to-indigo-500'; // Special color for trial
    }

    const navItems = [
        { name: 'Inicio', path: '/' },
        { name: 'Centro Inmobiliario', path: '/propiedades' },
        { name: 'Herramientas', path: '/centro-financiero' },
        { name: 'Planes', path: '/planes' },
        { name: 'Quiénes Somos', path: '/nosotros' },
        { name: 'Contacto', path: '/contacto' },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
        setIsMenuOpen(false);
    };

    return (
        <header className="sticky top-0 z-50 bg-[#0B1120] text-white shadow-lg border-b border-gray-800">
            <div className="container mx-auto px-4 py-4">
                {/* Top Section: Logo & Actions */}
                <div className="relative flex items-center justify-between md:justify-center mb-4">
                    {/* Mobile Menu Button (Left) */}
                    <button
                        className="md:hidden p-2 text-gray-300 hover:text-white"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                    >
                        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>

                    {/* Logo (Centered on Desktop, Left-ish on Mobile) */}
                    <Link to="/" className="flex flex-col items-center group">
                        <div className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
                            Servicios <span className="text-blue-500">Futura</span>
                            {user && (
                                <Badge className={`${planColor} text-[10px] px-2 py-0 h-4 border-0 font-black`}>
                                    {planLabel}
                                </Badge>
                            )}
                        </div>
                    </Link>

                    {/* Actions (Right) - Only show user avatar on mobile if logged in */}
                    <div className="flex items-center gap-2 md:absolute md:right-0">
                        {/* Favorites - Desktop only */}
                        <Link to="/favoritos" className="relative p-2 text-gray-300 hover:text-white transition-colors hidden md:block">
                            <Heart size={20} />
                            {favorites.length > 0 && (
                                <Badge className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 bg-red-500 text-[10px]">
                                    {favorites.length}
                                </Badge>
                            )}
                        </Link>

                        {/* Mobile: Show only user avatar if logged in, nothing if not (use hamburger) */}
                        {user && (
                            <div className="md:hidden h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
                                {user.full_name?.charAt(0)}
                            </div>
                        )}

                        {/* Desktop Auth Buttons */}
                        <div className="hidden md:flex items-center gap-2 ml-4">
                            {user ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                                            <div className="flex h-full w-full items-center justify-center rounded-full bg-blue-600 text-sm font-medium text-white">
                                                {user.full_name?.charAt(0) || <User className="h-4 w-4" />}
                                            </div>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-56" align="end" forceMount>
                                        <DropdownMenuLabel className="font-normal">
                                            <div className="flex flex-col space-y-1">
                                                <p className="text-sm font-medium leading-none">{user.full_name}</p>
                                                <p className="text-xs leading-none text-muted-foreground">
                                                    {user.email}
                                                </p>
                                            </div>
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                                            <LayoutDashboard className="mr-2 h-4 w-4" />
                                            <span>Dashboard</span>
                                        </DropdownMenuItem>
                                        {user.role === 'admin' && (
                                            <DropdownMenuItem onClick={() => navigate('/admin')}>
                                                <User className="mr-2 h-4 w-4" />
                                                <span>Admin Panel</span>
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={handleLogout}>
                                            <LogOut className="mr-2 h-4 w-4" />
                                            <span>Cerrar Sesión</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <>
                                    <Link to="/register">
                                        <Button className="bg-green-600 hover:bg-green-700 text-white font-bold shadow-md mr-2">
                                            Publicar propiedad
                                        </Button>
                                    </Link>
                                    <Link to="/login">
                                        <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-white/10">
                                            Iniciar Sesión
                                        </Button>
                                    </Link>
                                    <Link to="/register">
                                        <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                                            Registrarse
                                        </Button>
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center justify-center mb-4">
                    <nav className="flex justify-center items-center gap-1">
                        {navItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${location.pathname === item.path
                                    ? 'bg-white/10 text-white shadow-sm'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                {item.name}
                            </Link>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {isMenuOpen && (
                <>
                    {/* Background Overlay - Click to close */}
                    <div
                        className="md:hidden fixed inset-0 bg-black/50 z-40"
                        onClick={() => setIsMenuOpen(false)}
                    />
                    <div className="md:hidden fixed top-[72px] left-0 w-full bg-[#0B1120] border-b border-gray-800 shadow-xl animate-in slide-in-from-top-5 max-h-[80vh] overflow-y-auto z-50">
                        <nav className="flex flex-col p-4 gap-2">
                            {user ? (
                                <div className="mb-4 p-4 bg-gray-800/50 rounded-lg">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                                            {user.full_name?.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="font-medium text-white">{user.full_name}</p>
                                            <p className="text-xs text-gray-400">{user.email}</p>
                                        </div>
                                    </div>
                                    <Button
                                        className="w-full justify-start mb-2"
                                        variant="outline"
                                        onClick={() => { navigate('/dashboard'); setIsMenuOpen(false); }}
                                    >
                                        <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                                    </Button>
                                    {user.role === 'admin' && (
                                        <Button
                                            className="w-full justify-start mb-2 bg-purple-600 text-white hover:bg-purple-700 border-0"
                                            variant="outline"
                                            onClick={() => { navigate('/admin'); setIsMenuOpen(false); }}
                                        >
                                            <User className="mr-2 h-4 w-4" /> Admin Panel
                                        </Button>
                                    )}
                                    <Button
                                        className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                        variant="ghost"
                                        onClick={handleLogout}
                                    >
                                        <LogOut className="mr-2 h-4 w-4" /> Cerrar Sesión
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2 mb-4">
                                    <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                                        <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-bold">
                                            Publicar propiedad
                                        </Button>
                                    </Link>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                                            <Button variant="outline" className="w-full border-gray-600 text-gray-300 hover:text-white hover:bg-gray-800">
                                                Iniciar Sesión
                                            </Button>
                                        </Link>
                                        <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                                            <Button className="w-full bg-blue-600 hover:bg-blue-700">
                                                Registrarse
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            )}

                            <div className="h-px bg-gray-800 my-2"></div>

                            {navItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`p-3 rounded-lg text-sm font-medium transition-colors ${location.pathname === item.path
                                        ? 'bg-blue-600/20 text-blue-400'
                                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                                        }`}
                                >
                                    {item.name}
                                </Link>
                            ))}
                        </nav>
                    </div>
                </>
            )}
        </header>
    );
};

export default Header;
